import React, { useEffect, useState } from 'react';
import { getListingCategories, createListingCategory, updateListingCategory } from '../api/admin';

// Rules & Content > Marketplace Configuration — Categories.
// "The Owner must be able to add, rename, activate, deactivate, and reorder
// categories or subcategories without requiring code changes or developer
// support." This is that screen: listing_categories is plain admin-editable
// data, nothing here requires a deploy to add e.g. "Villas" or "Hostels"
// later.
const TYPE_OPTIONS = ['accommodation', 'venue', 'event'];
const BOOKING_MODE_OPTIONS = ['instant_book', 'request', 'ticket', 'reservation'];

const MarketplaceCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', icon: '', type: 'accommodation', booking_mode: 'instant_book', approval_required: false });
  const [error, setError] = useState('');

  const load = () => { setLoading(true); getListingCategories().then((res) => setCategories(res.data?.categories || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createListingCategory(form);
      setForm({ name: '', slug: '', icon: '', type: 'accommodation', booking_mode: 'instant_book', approval_required: false });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create category.');
    }
  };

  const toggleActive = async (cat) => { await updateListingCategory(cat.id, { is_active: !cat.is_active }); load(); };
  const rename = async (cat) => {
    const name = window.prompt('New name:', cat.name);
    if (name && name.trim() && name !== cat.name) { await updateListingCategory(cat.id, { name: name.trim() }); load(); }
  };
  const move = async (cat, direction) => {
    await updateListingCategory(cat.id, { sort_order: cat.sort_order + direction });
    load();
  };

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Marketplace Categories</h1>
      <p style={s.hint}>These power the Accommodation/Providers menu tabs (Hotels, Vacation Rentals, Wedding Halls, Events). Add new ones here any time — no code change needed.</p>

      <button style={s.newBtn} onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : '+ New category'}</button>

      {showForm && (
        <form onSubmit={create} style={s.form}>
          <input style={s.input} placeholder="Name (e.g. Villas)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          <input style={s.input} placeholder="Slug (e.g. villa)" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} required />
          <input style={s.input} placeholder="Icon (emoji, optional)" value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} />
          <select style={s.input} value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
            {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select style={s.input} value={form.booking_mode} onChange={(e) => setForm((f) => ({ ...f, booking_mode: e.target.value }))}>
            {BOOKING_MODE_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <label style={s.checkLabel}>
            <input type="checkbox" checked={form.approval_required} onChange={(e) => setForm((f) => ({ ...f, approval_required: e.target.checked }))} />
            Requires manual approval before booking (vs instant book)
          </label>
          {error && <div style={s.error}>{error}</div>}
          <button type="submit" style={s.saveBtn}>Create</button>
        </form>
      )}

      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr><th style={s.th}>Order</th><th style={s.th}>Category</th><th style={s.th}>Type</th><th style={s.th}>Booking mode</th><th style={s.th}>Active</th><th style={s.th}>Actions</th></tr></thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.id}>
                <td style={s.td}>
                  <button style={s.btnGray} onClick={() => move(c, -1)}>↑</button>
                  <button style={s.btnGray} onClick={() => move(c, 1)}>↓</button>
                </td>
                <td style={s.td}>{c.icon} {c.name} <span style={s.slug}>({c.slug})</span></td>
                <td style={s.td}>{c.type}</td>
                <td style={s.td}>{c.booking_mode}</td>
                <td style={s.td}><span style={{ ...s.badge, background: c.is_active ? '#dcfce7' : '#f3f4f6', color: c.is_active ? '#166534' : '#6b7280' }}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                <td style={s.td}>
                  <button style={s.btnGray} onClick={() => rename(c)}>Rename</button>
                  <button style={c.is_active ? s.btnRed : s.btnGreen} onClick={() => toggleActive(c)}>{c.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1000 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 16 },
  newBtn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 16 },
  form: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 400 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#374151' },
  error: { color: '#b91c1c', fontSize: 12.5 },
  saveBtn: { padding: '9px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: 'white', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  slug: { color: '#9ca3af', fontSize: 11 },
  badge: { padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
  btnGreen: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnRed: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
};

export default MarketplaceCategories;
